import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Scale, ArrowLeft, FileText, Download, Copy, Check,
  Printer, BookOpen, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { AuthContext } from "@/App";

// Use relative URL in production (deployed), or env variable in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MemoBuilder = () => {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const { user } = useContext(AuthContext);
  
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [memo, setMemo] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Form state
  const [matterTitle, setMatterTitle] = useState("");
  const [clientName, setClientName] = useState("");
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [buildDialogOpen, setBuildDialogOpen] = useState(false);

  useEffect(() => {
    if (chatId) {
      fetchChatData();
    }
  }, [chatId]);

  const fetchChatData = async () => {
    try {
      const messagesRes = await axios.get(`${BACKEND_URL}/api/chats/${chatId}/messages`);
      setMessages(messagesRes.data);
      
      // Get chat session info
      const chatsRes = await axios.get(`${BACKEND_URL}/api/chats`);
      const chatSession = chatsRes.data.find(c => c.chat_id === chatId);
      setChat(chatSession);
      
      if (chatSession?.title) {
        setMatterTitle(chatSession.title);
      }
    } catch (error) {
      console.error("Error fetching chat:", error);
      toast.error("Failed to load chat");
    } finally {
      setLoading(false);
    }
  };

  const handleBuildMemo = async () => {
    setGenerating(true);
    setBuildDialogOpen(false);
    
    try {
      const res = await axios.post(`${BACKEND_URL}/api/memo/build`, {
        chat_id: chatId,
        matter_title: matterTitle,
        client_name: clientName,
        additional_instructions: additionalInstructions
      });
      
      if (res.data.success) {
        setMemo(res.data);
        toast.success("Research memo generated!");
      } else {
        toast.error("Failed to generate memo");
      }
    } catch (error) {
      console.error("Memo build error:", error);
      toast.error("Failed to generate memo");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (memo?.memo) {
      navigator.clipboard.writeText(memo.memo);
      setCopied(true);
      toast.success("Memo copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (memo?.memo) {
      const blob = new Blob([memo.memo], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${matterTitle || 'research-memo'}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Memo downloaded");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="memo-builder-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/chat/${chatId}`)}
              data-testid="back-btn"
              className="text-[#94A3B8] hover:text-[#E2E8F0]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Scale className="w-6 h-6 text-[#C5A059]" />
              <span className="font-playfair text-lg font-semibold text-[#E2E8F0]">Research Memo Builder</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {memo && (
              <>
                <Button 
                  variant="outline"
                  onClick={handleCopy}
                  data-testid="copy-memo-btn"
                  className="border-white/20 text-[#E2E8F0] hover:bg-white/5"
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleDownload}
                  data-testid="download-memo-btn"
                  className="border-white/20 text-[#E2E8F0] hover:bg-white/5"
                >
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
              </>
            )}
            
            <Dialog open={buildDialogOpen} onOpenChange={setBuildDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="build-memo-btn"
                  className="bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] rounded-sm uppercase text-xs tracking-wide"
                >
                  <FileText className="w-4 h-4 mr-2" /> Build Memo
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#15171C] border-white/10">
                <DialogHeader>
                  <DialogTitle className="font-playfair text-[#E2E8F0]">Generate Research Memo</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-[#94A3B8] font-mono text-xs uppercase">Matter Title</Label>
                    <Input 
                      value={matterTitle}
                      onChange={(e) => setMatterTitle(e.target.value)}
                      placeholder="e.g., Duty of Care Analysis"
                      data-testid="memo-title-input"
                      className="mt-2 bg-[#0B0C10] border-white/10 text-[#E2E8F0]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] font-mono text-xs uppercase">Client Name (Optional)</Label>
                    <Input 
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="e.g., Confidential"
                      data-testid="memo-client-input"
                      className="mt-2 bg-[#0B0C10] border-white/10 text-[#E2E8F0]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] font-mono text-xs uppercase">Additional Instructions</Label>
                    <Textarea 
                      value={additionalInstructions}
                      onChange={(e) => setAdditionalInstructions(e.target.value)}
                      placeholder="Any specific formatting or focus areas..."
                      data-testid="memo-instructions-input"
                      className="mt-2 bg-[#0B0C10] border-white/10 text-[#E2E8F0] resize-none"
                      rows={3}
                    />
                  </div>
                  <Button 
                    onClick={handleBuildMemo}
                    data-testid="confirm-build-memo-btn"
                    className="w-full bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] rounded-sm uppercase text-xs tracking-wide"
                  >
                    Generate Memo
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-65px)]">
        {/* Left: Chat Summary */}
        <div className="w-80 border-r border-white/10 p-4">
          <h3 className="font-playfair text-lg text-[#E2E8F0] mb-4">Source Conversation</h3>
          <p className="text-[#64748B] font-mono text-xs mb-4">
            {messages.length} messages • {chat?.chat_type || 'research'}
          </p>
          
          <ScrollArea className="h-[calc(100vh-200px)]">
            <div className="space-y-3">
              {messages.map((msg, idx) => (
                <div 
                  key={msg.message_id || idx}
                  className={`p-3 rounded-sm ${
                    msg.role === 'user' 
                      ? 'bg-[#4A6FA5]/10 border-l-2 border-[#4A6FA5]' 
                      : 'bg-[#15171C] border-l-2 border-[#C5A059]'
                  }`}
                >
                  <p className="text-[#64748B] font-mono text-xs uppercase mb-1">
                    {msg.role === 'user' ? 'You' : 'TALC'}
                  </p>
                  <p className="text-[#E2E8F0] font-manrope text-sm line-clamp-3">
                    {msg.content}
                  </p>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Right: Memo Display */}
        <div className="flex-1 bg-[#15171C]">
          {generating ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 text-[#C5A059] mx-auto mb-4 animate-spin" />
                <h3 className="font-playfair text-xl text-[#E2E8F0] mb-2">Generating Memo</h3>
                <p className="text-[#94A3B8] font-manrope text-sm">
                  Converting your research conversation into a structured memorandum...
                </p>
              </div>
            </div>
          ) : memo ? (
            <ScrollArea className="h-full">
              <div className="p-8 max-w-4xl mx-auto">
                <div className="bg-[#0B0C10] border border-white/5 rounded-sm p-8">
                  <div className="prose prose-invert max-w-none">
                    <pre className="text-[#E2E8F0] font-manrope text-sm whitespace-pre-wrap leading-relaxed markdown-content">
                      {memo.memo}
                    </pre>
                  </div>
                </div>
                
                {memo.citations && memo.citations.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-playfair text-lg text-[#E2E8F0] mb-3">Referenced Authorities</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {memo.citations.map((cite, idx) => (
                        <Card key={idx} className="bg-[#0B0C10] border-white/5 p-3 rounded-sm">
                          <p className="font-mono text-xs text-[#C5A059]">
                            {cite.citation || cite.title}
                          </p>
                          <p className="text-[#94A3B8] text-xs mt-1">
                            {cite.name || cite.type}
                          </p>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <BookOpen className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                <h3 className="font-playfair text-xl text-[#E2E8F0] mb-2">Build Research Memo</h3>
                <p className="text-[#94A3B8] font-manrope text-sm mb-4">
                  Convert this legal research conversation into a professionally structured 
                  memorandum with proper citations and IRAC analysis.
                </p>
                <Button 
                  onClick={() => setBuildDialogOpen(true)}
                  className="bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] rounded-sm uppercase text-xs tracking-wide"
                >
                  Get Started
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemoBuilder;
